import React from "react";
import { AdminMetricCard } from "./AdminMetricCard";
import {
  Users,
  FolderKanban,
  Sparkles,
  Rocket,
  MessageSquare,
  UserCheck,
  Activity,
  CheckCircle,
  XCircle,
} from "lucide-react";

interface AdminMetricsGridProps {
  userMetrics: {
    total_users: number;
    daily_active_users: number;
    weekly_active_users: number;
    monthly_active_users: number;
    onboarding_completion_pct: number;
    retention_7d_pct: number;
    retention_30d_pct: number;
    new_users_today: number;
    new_users_week: number;
    new_users_month: number;
  } | null;
  projectMetrics: {
    total_projects: number;
    projects_today: number;
    projects_week: number;
    projects_month: number;
    avg_files_per_project: number;
    avg_ai_actions_per_project: number;
    projects_with_deploys: number;
  } | null;
  aiMetrics: {
    total_requests: number;
    requests_today: number;
    requests_week: number;
    avg_requests_per_user: number;
    scaffold_count: number;
    code_agent_count: number;
    avg_tokens_per_request: number;
    success_count: number;
    failure_count: number;
    success_rate: number;
  } | null;
  deployMetrics: {
    total_deploys: number;
    deploys_today: number;
    deploys_week: number;
    successful_deploys: number;
    failed_deploys: number;
    success_rate: number;
    avg_deploys_per_user: number;
    unique_deployers: number;
  } | null;
  collaborationMetrics: {
    projects_with_collaborators: number;
    avg_collaborators_per_project: number;
    total_chat_messages: number;
    messages_today: number;
    messages_week: number;
    active_collaborations: number;
    total_invites: number;
    accepted_invites: number;
  } | null;
}

export function AdminMetricsGrid({
  userMetrics,
  projectMetrics,
  aiMetrics,
  deployMetrics,
  collaborationMetrics,
}: AdminMetricsGridProps) {
  return (
    <div className="space-y-8">
      {/* User Metrics */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Users className="h-5 w-5" /> User Metrics
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <AdminMetricCard
            title="Total Users"
            value={userMetrics?.total_users ?? 0}
            subtitle={`+${userMetrics?.new_users_today ?? 0} today`}
            icon={<Users className="h-4 w-4" />}
          />
          <AdminMetricCard
            title="DAU"
            value={userMetrics?.daily_active_users ?? 0}
            subtitle="Daily Active Users"
            icon={<Activity className="h-4 w-4" />}
          />
          <AdminMetricCard
            title="WAU"
            value={userMetrics?.weekly_active_users ?? 0}
            subtitle="Weekly Active Users"
          />
          <AdminMetricCard
            title="MAU"
            value={userMetrics?.monthly_active_users ?? 0}
            subtitle="Monthly Active Users"
          />
          <AdminMetricCard
            title="Onboarding %"
            value={`${userMetrics?.onboarding_completion_pct ?? 0}%`}
            subtitle="Completed onboarding"
            icon={<UserCheck className="h-4 w-4" />}
          />
          <AdminMetricCard
            title="7-Day Retention"
            value={`${userMetrics?.retention_7d_pct ?? 0}%`}
            subtitle="Users returning after 7 days"
          />
          <AdminMetricCard
            title="30-Day Retention"
            value={`${userMetrics?.retention_30d_pct ?? 0}%`}
            subtitle="Users returning after 30 days"
          />
          <AdminMetricCard
            title="New This Week"
            value={userMetrics?.new_users_week ?? 0}
            subtitle="New users (7 days)"
          />
          <AdminMetricCard
            title="New This Month"
            value={userMetrics?.new_users_month ?? 0}
            subtitle="New users (30 days)"
          />
        </div>
      </div>

      {/* Project Metrics */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FolderKanban className="h-5 w-5" /> Project Metrics
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <AdminMetricCard
            title="Total Projects"
            value={projectMetrics?.total_projects ?? 0}
            subtitle={`+${projectMetrics?.projects_today ?? 0} today`}
            icon={<FolderKanban className="h-4 w-4" />}
          />
          <AdminMetricCard
            title="Projects This Week"
            value={projectMetrics?.projects_week ?? 0}
            subtitle="Created in last 7 days"
          />
          <AdminMetricCard
            title="Avg Files/Project"
            value={projectMetrics?.avg_files_per_project ?? 0}
            subtitle="Average file count"
          />
          <AdminMetricCard
            title="Avg AI Actions/Project"
            value={projectMetrics?.avg_ai_actions_per_project ?? 0}
            subtitle="AI interactions per project"
          />
          <AdminMetricCard
            title="Projects With Deploys"
            value={projectMetrics?.projects_with_deploys ?? 0}
            subtitle="Successfully deployed"
          />
        </div>
      </div>

      {/* AI Usage Metrics */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Sparkles className="h-5 w-5" /> AI Usage Metrics
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <AdminMetricCard
            title="Total AI Requests"
            value={aiMetrics?.total_requests ?? 0}
            subtitle={`+${aiMetrics?.requests_today ?? 0} today`}
            icon={<Sparkles className="h-4 w-4" />}
          />
          <AdminMetricCard
            title="Requests/User"
            value={aiMetrics?.avg_requests_per_user ?? 0}
            subtitle="Average per user"
          />
          <AdminMetricCard
            title="Scaffolds"
            value={aiMetrics?.scaffold_count ?? 0}
            subtitle="App scaffold generations"
          />
          <AdminMetricCard
            title="Code Agent"
            value={aiMetrics?.code_agent_count ?? 0}
            subtitle="Code agent actions"
          />
          <AdminMetricCard
            title="Avg Tokens"
            value={Math.round(aiMetrics?.avg_tokens_per_request ?? 0)}
            subtitle="Per request"
          />
          <AdminMetricCard
            title="Success Rate"
            value={`${aiMetrics?.success_rate ?? 0}%`}
            icon={<CheckCircle className="h-4 w-4 text-green-500" />}
          />
          <AdminMetricCard
            title="Successes"
            value={aiMetrics?.success_count ?? 0}
            icon={<CheckCircle className="h-4 w-4 text-green-500" />}
          />
          <AdminMetricCard
            title="Failures"
            value={aiMetrics?.failure_count ?? 0}
            icon={<XCircle className="h-4 w-4 text-red-500" />}
          />
        </div>
      </div>

      {/* Deploy Metrics */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Rocket className="h-5 w-5" /> Deploy Metrics
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <AdminMetricCard
            title="Total Deploys"
            value={deployMetrics?.total_deploys ?? 0}
            subtitle={`+${deployMetrics?.deploys_today ?? 0} today`}
            icon={<Rocket className="h-4 w-4" />}
          />
          <AdminMetricCard
            title="Success Rate"
            value={`${deployMetrics?.success_rate ?? 0}%`}
            icon={<CheckCircle className="h-4 w-4 text-green-500" />}
          />
          <AdminMetricCard
            title="Successful"
            value={deployMetrics?.successful_deploys ?? 0}
            icon={<CheckCircle className="h-4 w-4 text-green-500" />}
          />
          <AdminMetricCard
            title="Failed"
            value={deployMetrics?.failed_deploys ?? 0}
            icon={<XCircle className="h-4 w-4 text-red-500" />}
          />
          <AdminMetricCard
            title="Deploys/User"
            value={deployMetrics?.avg_deploys_per_user ?? 0}
            subtitle="Average per user"
          />
          <AdminMetricCard
            title="Unique Deployers"
            value={deployMetrics?.unique_deployers ?? 0}
            subtitle="Users who deployed"
          />
        </div>
      </div>

      {/* Collaboration Metrics */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <MessageSquare className="h-5 w-5" /> Collaboration Metrics
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <AdminMetricCard
            title="Collaborative Projects"
            value={collaborationMetrics?.projects_with_collaborators ?? 0}
            subtitle="Projects with team members"
            icon={<Users className="h-4 w-4" />}
          />
          <AdminMetricCard
            title="Avg Collaborators"
            value={collaborationMetrics?.avg_collaborators_per_project ?? 0}
            subtitle="Per project"
          />
          <AdminMetricCard
            title="Total Messages"
            value={collaborationMetrics?.total_chat_messages ?? 0}
            subtitle={`+${collaborationMetrics?.messages_today ?? 0} today`}
            icon={<MessageSquare className="h-4 w-4" />}
          />
          <AdminMetricCard
            title="Active Collaborations"
            value={collaborationMetrics?.active_collaborations ?? 0}
            subtitle="Projects with recent activity"
          />
          <AdminMetricCard
            title="Total Invites"
            value={collaborationMetrics?.total_invites ?? 0}
            subtitle="Invitations sent"
          />
          <AdminMetricCard
            title="Accepted Invites"
            value={collaborationMetrics?.accepted_invites ?? 0}
            subtitle="Invitations accepted"
          />
        </div>
      </div>
    </div>
  );
}
